/**
 * ICAO 3-letter airline code → official English name.
 *
 * Curated for airlines a Japan-based ADS-B receiver is most likely to see
 * (Japanese flag carriers, Asian regional carriers, major intercontinental).
 * Source: factual ICAO/IATA airline registrations — public domain data, no
 * external license obligation.
 *
 * If a code isn't in the map, callers should fall back to displaying the
 * raw code so unknown carriers are still legible.
 */

const ICAO_TO_NAME: Record<string, string> = {
  // Japan
  ANA: "All Nippon Airways",
  AKX: "ANA Wings",
  JAL: "Japan Airlines",
  JNA: "J-Air",
  ADO: "Air Do",
  SKY: "Skymark Airlines",
  SFJ: "StarFlyer",
  APJ: "Peach Aviation",
  JJP: "Jetstar Japan",
  AJX: "ANA Cargo",
  AHX: "Amakusa Airlines",
  IBX: "IBEX Airlines",
  RAC: "Ryukyu Air Commuter",
  ORC: "Oriental Air Bridge",
  NCA: "Nippon Cargo Airlines",
  TZP: "Zipair Tokyo",
  SJO: "Spring Airlines Japan",
  EXC: "ExecuJet",

  // Greater China / Taiwan
  CAL: "China Airlines",
  EVA: "EVA Air",
  CES: "China Eastern Airlines",
  CSN: "China Southern Airlines",
  CCA: "Air China",
  CSH: "Shanghai Airlines",
  CXA: "Xiamen Airlines",
  HDA: "Hong Kong Dragon Airlines",
  CPA: "Cathay Pacific",
  CRK: "Hong Kong Airlines",
  CHH: "Hainan Airlines",
  CSC: "Sichuan Airlines",
  CSZ: "Shenzhen Airlines",
  CQH: "Spring Airlines",
  CDG: "Shandong Airlines",
  TBA: "Tibet Airlines",
  TTW: "Tigerair Taiwan",
  STR: "Starlux Airlines",
  UIA: "Uni Air",
  MDA: "Mandarin Airlines",
  HKE: "Hong Kong Express",

  // Korea
  KAL: "Korean Air",
  AAR: "Asiana Airlines",
  JNA_KR: "Jin Air",
  JJA: "Jeju Air",
  TWB: "T'way Air",
  ABL: "Air Busan",
  ESR: "Eastar Jet",
  AIH: "Air Incheon",
  APZ: "Air Premia",
  ASV: "Air Seoul",

  // SE Asia
  SIA: "Singapore Airlines",
  SLK: "Scoot",
  THA: "Thai Airways International",
  TAX: "Thai AirAsia X",
  TLM: "Thai Lion Air",
  THD: "Thai Smile",
  AIQ: "Thai AirAsia",
  AXM: "AirAsia",
  XAX: "AirAsia X",
  MAS: "Malaysia Airlines",
  PAL: "Philippine Airlines",
  CEB: "Cebu Pacific",
  HVN: "Vietnam Airlines",
  VJC: "VietJet Air",
  GIA: "Garuda Indonesia",
  CTV: "Citilink",
  LNI: "Lion Air",
  SJY: "Sriwijaya Air",
  RBA: "Royal Brunei Airlines",
  MAH: "Malindo Air",

  // Oceania
  QFA: "Qantas",
  JST: "Jetstar Airways",
  ANZ: "Air New Zealand",
  VOZ: "Virgin Australia",
  RXA: "Regional Express",
  FJI: "Fiji Airways",

  // North America
  UAL: "United Airlines",
  AAL: "American Airlines",
  DAL: "Delta Air Lines",
  ASA: "Alaska Airlines",
  HAL: "Hawaiian Airlines",
  ACA: "Air Canada",
  WJA: "WestJet",
  FFT: "Frontier Airlines",
  SWA: "Southwest Airlines",
  JBU: "JetBlue Airways",
  NKS: "Spirit Airlines",
  GTI: "Atlas Air",
  FDX: "FedEx Express",
  UPS: "United Parcel Service",
  ABX: "ABX Air",
  ATN: "Air Transport International",

  // Europe
  BAW: "British Airways",
  VIR: "Virgin Atlantic",
  EZY: "easyJet",
  RYR: "Ryanair",
  DLH: "Lufthansa",
  GEC: "Lufthansa Cargo",
  AFR: "Air France",
  AFL: "Aeroflot",
  KLM: "KLM Royal Dutch Airlines",
  AUA: "Austrian Airlines",
  SWR: "Swiss International Air Lines",
  TAP: "TAP Air Portugal",
  IBE: "Iberia",
  AZA: "ITA Airways",
  FIN: "Finnair",
  THY: "Turkish Airlines",
  SAS: "Scandinavian Airlines",
  AEA: "Air Europa",
  EIN: "Aer Lingus",
  ELY: "El Al Israel Airlines",

  // Middle East
  UAE: "Emirates",
  ETD: "Etihad Airways",
  QTR: "Qatar Airways",
  THY_TR: "Turkish Airlines",
  SVA: "Saudia",
  GFA: "Gulf Air",
  KAC: "Kuwait Airways",
  OMA: "Oman Air",

  // India
  AIC: "Air India",
  IGO: "IndiGo",
  SEJ: "SpiceJet",
  AXB: "Air India Express",
  IAD: "Akasa Air",

  // Cargo/charter
  ABW: "AirBridgeCargo Airlines",
  CKK: "China Cargo Airlines",
  GTI_FX: "Polar Air Cargo",
  PAC: "Polar Air Cargo",
  CLX: "Cargolux",
  CKS: "Kalitta Air",

  // Russia / CIS / Mongolia
  AFL_RU: "Aeroflot Russian Airlines",
  SBI: "S7 Airlines",
  MGL: "MIAT Mongolian Airlines",

  // Generic / often-seen utility
  GA: "General Aviation",
};

/**
 * @returns the official English name if `icao` is a known 3-letter ICAO
 * airline code, or `null` if unknown. Caller decides whether to fall back
 * to the raw code or to "—".
 */
export function airlineName(icao: string | null | undefined): string | null {
  if (!icao) return null;
  const key = icao.toUpperCase().trim();
  return ICAO_TO_NAME[key] ?? null;
}
